import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { openPdfResponse } from "../../utils/openPdfInApp";
import {fetchPDF} from "../../services/PurchaseOrderServices";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import ListFormPageHeader from "../../components/ListFormPageHeader";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { getAllPurchaseOrders} from "../../services/PurchaseOrderServices";
import { getListViewSettings } from "../../services/SettingsServices";
import { displayError } from "../../helpers";
import { buildPurchaseOrderQueryParams, DEFAULT_LIST_VIEW_CONFIG } from "../../utils/listViewDateRanges";
import { getDefaultListViewConfig } from "../../config/listViewSettingsConfig";

function PurchaseOrderTable() {
    const InitialData = {
        total_pages: 0,
        records: [],
      };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate(); 
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);  
    const columns = useMemo(
        () => [
            {
                fieldName: "company_id",
                headerName: 'Office'
            },
            {
                fieldName: "order_no",
                headerName: 'PurchaseOrder no'
            },
            {
                fieldName: 'order_date', 
                headerName: 'Date'
            },
            {
                fieldName: 'vendor_id', 
                headerName: 'Vendor'
            },
            {
                fieldName: 'quantity', 
                headerName: 'Order Qty'
            },
            {
                fieldName: 'product_id', 
                headerName: 'Product Name'
            },
            {
                fieldName: 'unit', 
                headerName: 'Unit'
            },
            {
                fieldName: "rate",
                headerName: 'Rate'
            },
            {
                fieldName: "amount",
                headerName: 'Amount'
            },
            {
                fieldName: 'is_approved', 
                headerName: 'Approval Status'
            },
            
            {
                fieldName: "id",
                headerName: "ROW_ACTIONS"
            }
        ],
        [],);

    const mapPurchaseOrderRows = useCallback((purchaseOrderList) => (
        (purchaseOrderList || []).map((PO) => ({
            id: PO.id,
            company_id: PO.company?.name ?? '',
            order_no: `${PO.prefix ?? ''}${PO.order_no ?? ''}`,
            order_date: PO.order_date ?? '',
            vendor_id: PO.vendor?.name ?? '',
            quantity: PO.quantity ?? '',
            product_id: PO.product?.name ?? '',
            unit: PO.product?.unit?.symbol ?? '',
            rate: PO.rate ?? '',
            amount: PO.amount ?? '',
            is_approved: PO.is_approved ? 'Approved' : 'Waiting',
        }))
    ), []);

    const loadPurchaseOrders = useCallback((config) => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return;
        }
        const activeConfig = config || getDefaultListViewConfig('purchase_order') || DEFAULT_LIST_VIEW_CONFIG;
        setIsLoading(true);
        getAllPurchaseOrders(cookies, buildPurchaseOrderQueryParams(activeConfig))
            .then((list) => {
                setData({
                    records: mapPurchaseOrderRows(list?.purchase_order_list),
                });
            })
            .catch((error) => {
                displayError(error?.response?.data ?? error.message, 'Failed to load purchase orders');
                setData({ records: [] });
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [cookies, mapPurchaseOrderRows]);

    const handleSettingsSaved = useCallback((config) => {
        loadPurchaseOrders(config);
    }, [loadPurchaseOrders]);

    const onEditPurchaseOrder = (PurchaseOrderId, event) => {
       
       navigate(`/PurchaseOrderEdit/${PurchaseOrderId}/`);
    };
    const onDeleteQuotation = (quotationId, event) => {

        event.preventDefault();
        Swal.fire({title: 'Are you sure to Delete?',  
        showCancelButton: true,  
        confirmButtonText: `Yes`,  
        cancelButtonText: `No`,
        }).then((result) => {  
          if (result.isConfirmed) { 
            setIsLoading(true);
            // deleteQuotation(cookies, quotationId); 
        console.log(`Quotation with id ${quotationId} deleted`);
            Swal.fire('Deleted Successfully!', '', 'success');
            setRefreshKey(oldKey => oldKey +1)
          } else if (result.isDismissed) {    
            Swal.fire('Not Deleted', '', 'info')  
        }
        });
        

    };

    const handleCSVExport = (event) => {
        console.log('CSV Export');
        exportAsCSV(
            columns.filter(col => col.headerName !== 'ROW_ACTIONS'),
            data.records
        );
    };

    const handlePDFExport = (event) => {
        console.log('PDF Export');
        exportAsPDF(
            'List of PurchaseOrder',
            columns
            .filter(col => col.headerName !== 'ROW_ACTIONS')
            .map(col=>({header: col.headerName, dataKey: col.fieldName })),
            data.records.map(POpdf => ({
                id: POpdf.id, 
                company_id: POpdf.company_id,
                order_no: POpdf.order_no,
                order_date:POpdf.order_date,
                vendor_id: POpdf.vendor_id,
                quantity: POpdf.quantity,
                product_id: POpdf.product_id,
                unit:POpdf.unit,
                rate: POpdf.rate,
                amount: POpdf.amount,
                is_approved: POpdf.is_approved,


            })),
            'PO.pdf'
        );
    };

    const handlePrint = (purchaseOrderId, event) => {

        setIsLoading(true);
        fetchPDF(cookies, purchaseOrderId)
        .then(response => {
            openPdfResponse(response, {
                title: 'Purchase Order',
                downloadFileName: 'purchase_order.pdf',
            });
            setIsLoading(false);
        })
        .catch(error => {
            console.error(error);
            displayError(error, 'Custom print/export failed');
            setIsLoading(false);
        });
    };

    useEffect(() => {
        getListViewSettings(cookies, 'purchase_order')
            .then((response) => {
                loadPurchaseOrders(response.config || getDefaultListViewConfig('purchase_order'));
            })
            .catch(() => {
                loadPurchaseOrders(getDefaultListViewConfig('purchase_order'));
            });
    }, [cookies, loadPurchaseOrders, refreshKey]);
    
    return (

        <div id="outer-container"  className="App" > 
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
            <ListFormPageHeader
                module="PURCHASE"
                title="List of Purchase Order"
                viewKey="purchase_order"
                onSettingsSaved={handleSettingsSaved}
                formLink="/PurchaseOrder"
                addNewLabel="New Purchase Order"
            />
                <div className="container item-list-table-container">
                <div className="table-responsive">
                <ReactTableWrapper
                            title='List of PurchaseOrder'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditPurchaseOrder}
                            onRowDelete={onDeleteQuotation}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            onRowPrint={handlePrint}
                            hidePrint ={false}
                            isLoading={isLoading}
                            hideDelete={true}
                        />
                </div>
                </div> 


            </div>
            <button style={{position:"relative",right:"40px"}}className="btn btn-fill btn-primary float-end" type="button" onClick={() =>{navigate("/PurchaseOrder")}}>Back</button>
        </div>

        
    );

}



export default PurchaseOrderTable;
